import {
  BlockLayoutType,
  ServerBlockTypeType,
  getServerBlockType
} from "@pathwright/blocks-core"
import {
  BlockDataType,
  BlockType,
  EDITOR_LAYERING,
  EDITOR_MODE,
  useEditorStateSelector,
  useEditorUI
} from "@pathwright/blocks-engine"
import isEqual from "react-fast-compare"
import ErrorBoundary from "@pathwright/ui/src/components/error/ErrorBoundary"
import React from "react"
import styled, { keyframes } from "styled-components"
import AddBlocks from "./AddBlocks"
import BlockControls from "./BlockControls"
import BlockEditor from "./BlockEditor"
import BlockErrorHandler from "./BlockErrorHandlerLazy"
import { defaultBlockLayoutStyles } from "./BlockStyles/base-style-defaults"
import DeleteBlockPrompt from "./DeleteBlockPrompt"

const blockIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const StyledBlockEditorContainerWrap = styled.div<{ $isFocused: boolean }>`
  position: relative;
  z-index: ${(p) => (p.$isFocused ? EDITOR_LAYERING.FOCUSED_BLOCK : "")};

  .ContentBlock {
    animation: ${blockIn} 0.33s;
  }

  /* Prevent menus (ex: Styles) from remaining open over a subsequent block */
  &:not(:hover) .BlockControls {
    display: none;
  }

  /* Keep controls mounted while a portaled popover menu is open */
  &:not(:hover) .BlockControls:has([data-state="open"]) {
    display: flex;
    opacity: 1;
    z-index: ${EDITOR_LAYERING.HOVERED_BLOCK_CONTROLS};
  }

  .BlockControls {
    opacity: 0;
    pointer-events: none;
  }

  &:hover {
    .AddBlocks {
      opacity: 1;
      &:not(.AddBlocks--open) .AddBlocks__Icon {
        z-index: ${EDITOR_LAYERING.HOVERED_ADD_BLOCK};
      }
    }

    .BlockControls {
      opacity: 1;
      z-index: ${EDITOR_LAYERING.HOVERED_BLOCK_CONTROLS};
    }
  }
`

type BlockEditorContainerWrapProps = {
  children: React.ReactNode
  blockIndex: number
  className: string
  id: string
  isURLFocused: boolean
}

const BlockEditorContainerWrap = ({
  children,
  ...props
}: BlockEditorContainerWrapProps) => {
  const { focusedBlockIndex } = useEditorUI()

  const isFocused = focusedBlockIndex === props.blockIndex

  return (
    <StyledBlockEditorContainerWrap {...props} $isFocused={isFocused}>
      {children}
    </StyledBlockEditorContainerWrap>
  )
}

// Wrapper component that memoizes render functions to prevent unnecessary re-renders
type BlockEditorContainerWrapperProps = {
  block: BlockType
  deleting: boolean
  blockType: ServerBlockTypeType
  blockIndex: number
  onCopyBlock: (block?: BlockType | null) => void
  showAddBlocks: boolean
  isLastBlock: boolean
  onAddBlock: (
    blockIndex: number
  ) => (
    type: string,
    data: Record<string, any>,
    layout: BlockLayoutType["key"]
  ) => void
}

const BlockEditorContainerWrapper = (
  props: BlockEditorContainerWrapperProps
) => {
  const {
    block,
    deleting,
    blockType,
    blockIndex,
    onCopyBlock,
    showAddBlocks,
    isLastBlock,
    onAddBlock
  } = props

  // Memoize the render functions so they have stable references
  const renderTopAddBlocks = React.useCallback(() => {
    if (!showAddBlocks) return null

    return (
      <AddBlocks
        key={`add-blocks-top-${blockIndex}`}
        onAddBlock={onAddBlock(blockIndex)}
        blockIndex={blockIndex}
        top={true}
        forceOpen={false}
        onCancelPaste={() => onCopyBlock(null)}
        isLastBlock={isLastBlock}
      />
    )
  }, [blockIndex, showAddBlocks, isLastBlock, onAddBlock, onCopyBlock])

  const renderBottomAddBlocks = React.useCallback(() => {
    if (!showAddBlocks) return null

    return (
      <AddBlocks
        key={`add-blocks-bottom-${blockIndex + 1}`}
        onAddBlock={onAddBlock(blockIndex + 1)}
        blockIndex={blockIndex + 1}
        top={false}
        forceOpen={false}
        onCancelPaste={() => onCopyBlock(null)}
        isLastBlock={isLastBlock}
      />
    )
  }, [blockIndex, showAddBlocks, isLastBlock, onAddBlock, onCopyBlock])

  return (
    <BlockEditorContainer
      block={block}
      deleting={deleting}
      blockType={blockType}
      blockIndex={blockIndex}
      onCopyBlock={onCopyBlock}
      renderTopAddBlocks={renderTopAddBlocks}
      renderBottomAddBlocks={renderBottomAddBlocks}
    />
  )
}

const propsAreEqual = (
  prevProps: BlockEditorContainerProps,
  nextProps: BlockEditorContainerProps
) => {
  if (nextProps.deleting !== prevProps.deleting) return false
  if (nextProps.blockIndex !== prevProps.blockIndex) return false
  if (!isEqual(nextProps.blockType, prevProps.blockType)) return false
  if (!isEqual(nextProps.block, prevProps.block)) return false

  return true
}

type BlockEditorContainerProps = {
  block: BlockType
  deleting: boolean
  blockType: ServerBlockTypeType
  blockIndex: number
  onCopyBlock: (block?: BlockType | null) => void
  renderTopAddBlocks: () => React.ReactNode
  renderBottomAddBlocks: () => React.ReactNode
}

const BlockEditorContainer = React.memo((props: BlockEditorContainerProps) => {
  const {
    block,
    deleting,
    blockType,
    blockIndex,
    onCopyBlock,
    renderTopAddBlocks,
    renderBottomAddBlocks
  } = props

  const hash = window.location.hash.replace("/", "").replace("#", "")

  const mode = useEditorStateSelector((state) => state.mode)
  const onMoveUp = useEditorStateSelector((state) => state.handleMoveUp)
  const onMoveDown = useEditorStateSelector((state) => state.handleMoveDown)
  const onChangeLayout = useEditorStateSelector(
    (state) => state.handleLayoutChange
  )
  const onDelete = useEditorStateSelector((state) => state.handleBlockDelete)
  const onCancelDelete = useEditorStateSelector(
    (state) => state.handleCancelBlockDelete
  )
  const onBlockChange = useEditorStateSelector(
    (state) => state.handleBlockChange
  )

  const handleMoveUp = React.useCallback(() => {
    onMoveUp(blockIndex, block.id)
  }, [blockIndex, block.id, onMoveUp])

  const handleMoveDown = React.useCallback(() => {
    onMoveDown(blockIndex, block.id)
  }, [blockIndex, block.id, onMoveDown])

  const handleDelete = React.useCallback(() => {
    onDelete(block.id)
  }, [block.id, onDelete])

  const handleCopy = React.useCallback(() => {
    onCopyBlock(block)
  }, [block, onCopyBlock])

  const handleSelectLayout = React.useCallback(
    (layout: BlockLayoutType["key"]) => {
      onChangeLayout(block.id, layout)
    },
    [block.id, onChangeLayout]
  )

  const handleCancel = React.useCallback(() => {
    onCancelDelete(blockIndex)
  }, [blockIndex])

  const handleBlockDataChange = React.useCallback(
    (data: BlockDataType) => {
      onBlockChange(data, block.id)
    },
    [block.id, onBlockChange]
  )

  return (
    <BlockEditorContainerWrap
      key={`${block.contentID}:${block.id}`}
      className={`BlockEditorContainer block-mode-${mode} Block-${blockIndex} block-type-${block.type}-${block.layout}`}
      blockIndex={blockIndex}
      id={`block-${block.id}`}
      isURLFocused={hash === block.id}
    >
      {deleting ? (
        <DeleteBlockPrompt onCancel={handleCancel} onDelete={handleDelete} />
      ) : null}
      {renderTopAddBlocks()}
      {mode === EDITOR_MODE.EDIT ? (
        <BlockControls
          blockIndex={blockIndex}
          onCopy={handleCopy}
          onDelete={handleDelete}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onSelectLayout={handleSelectLayout}
          block={block}
          blockType={blockType}
        />
      ) : null}
      <BlockEditor
        blockIndex={blockIndex}
        blockType={blockType}
        block={block}
        onChange={handleBlockDataChange}
      />
      {renderBottomAddBlocks()}
    </BlockEditorContainerWrap>
  )
}, propsAreEqual)

type BlockRowProps = {
  block: BlockType
  blockType: ServerBlockTypeType
  deleting: boolean
  showAddBlocks: boolean
  isLastBlock: boolean
  blockIndex: number
  onAddBlock: (
    blockIndex: number
  ) => (
    type: string,
    data: BlockDataType,
    layout: BlockLayoutType["key"]
  ) => void
  onCopyBlock: (block?: BlockType | null) => void
}

const BlockRow = React.memo(function BlockRow(props: BlockRowProps) {
  const { block, blockType, ...rest } = props

  const renderError = React.useCallback(
    (error: Error, resetError: () => void) => (
      <BlockErrorHandler
        error={error}
        block={block}
        allowEdit
        blockType={blockType.type}
        onFixedBlock={resetError}
      />
    ),
    [block, blockType]
  )

  return (
    <ErrorBoundary catchLoadingChunkFailures={false} renderError={renderError}>
      <BlockEditorContainerWrapper
        block={block}
        blockType={blockType}
        {...rest}
      />
    </ErrorBoundary>
  )
})

const ContentEditorBlocks = () => {
  const mode = useEditorStateSelector((state) => state.mode)
  const deleteBlockID = useEditorStateSelector((state) => state.deleteBlockID)
  const blocks = useEditorStateSelector((state) => state.blocks)
  const blockTypes = useEditorStateSelector((state) => state.blockTypes)
  const handleAddBlock = useEditorStateSelector((state) => state.handleAddBlock)
  const handleCopyBlock = useEditorStateSelector(
    (state) => state.handleCopyBlock
  )

  // Memoize handleAddBlock to prevent recreating it on every render
  const memoizedHandleAddBlock = React.useCallback(
    (blockIndex: number) =>
      (type: string, data: BlockDataType, layout: BlockLayoutType["key"]) => {
        const defaultStyles =
          defaultBlockLayoutStyles[
            layout as keyof typeof defaultBlockLayoutStyles
          ] || null
        handleAddBlock(type, blockIndex + 1, data, layout, defaultStyles)
      },
    [handleAddBlock]
  )

  const renderAddBlocks = (
    blockIndex: number,
    placement: "top" | "bottom",
    show: boolean,
    {
      forceOpen = false,
      isLastBlock = false
    }: { forceOpen?: boolean; isLastBlock?: boolean }
  ) => {
    if (!show) return null

    return (
      <AddBlocks
        key={`add-blocks-${placement}-${blockIndex}`}
        onAddBlock={memoizedHandleAddBlock(blockIndex)}
        blockIndex={blockIndex}
        top={placement === "top"}
        forceOpen={forceOpen}
        onCancelPaste={() => handleCopyBlock(null)}
        isLastBlock={isLastBlock}
      />
    )
  }

  return (
    <div
      style={{ position: "relative", zIndex: "1" }}
      className="ContentEditorBlocks"
    >
      {blocks && blocks.length
        ? blocks.map((block, i) => {
            const isLastBlock = i === blocks.length - 1

            const deleting = deleteBlockID === block.id
            const showAddBlocks = mode === EDITOR_MODE.EDIT && !deleting

            const blockType = block.type
              ? getServerBlockType(block.type, blockTypes)
              : null
            if (!blockType) {
              console.error(`Unknown block type found: ${block.type}`)
              return null
            }
            return (
              <BlockRow
                key={block.id}
                block={block}
                blockType={blockType}
                blockIndex={i}
                deleting={deleting}
                showAddBlocks={showAddBlocks}
                isLastBlock={isLastBlock}
                onAddBlock={memoizedHandleAddBlock}
                onCopyBlock={handleCopyBlock}
              />
            )
          })
        : renderAddBlocks(0, "top", true, {
            forceOpen: true
          })}
    </div>
  )
}

export default ContentEditorBlocks
